// helpers/EventHelpers.js

import { DataStore } from "@aws-amplify/datastore";
import { Events, ModelType, LogCategory } from "../models"; // adjust path to match your project structure
import { EventsSourceType } from "../Enums"; // adjust path to match your project structure
import { saveAssetsBatch } from "../asset/AssetsRegistry";

export async function logAdminAction({
  event = "AdminActionOccurred",
  sourceType = EventsSourceType.USER,
  sourceId,
  userId,
  projectToken,
  isTicker = false,
  isSuperAdmin = false,
  payload: businessPayload,
}) {
  try {
    // 1) Top-level validation
    const topLevel = { event, sourceType, userId, projectToken };
    for (const [k, v] of Object.entries(topLevel)) {
      if (v == null || v === "") {
        throw new Error(`logAdminAction: Missing or invalid \`${k}\``);
      }
    }

    // 1b) Derive sourceId here (required by Events schema)
    const finalSourceId = sourceId ?? `${sourceType}:${userId}`;
    if (!finalSourceId) {
      throw new Error("logAdminAction: Missing or invalid `sourceId`");
    }

    // 2) Payload object required
    if (!businessPayload || typeof businessPayload !== "object") {
      throw new Error("logAdminAction: Missing or invalid `payload` object");
    }

    // 3) Extract + validate required business payload fields
    const {
      source,
      type,
      category,
      entityId,
      description,
      logData,
      modelType,
      ...extras
    } = businessPayload;

    const requiredPayload = { source, type, category, entityId };
    for (const [k, v] of Object.entries(requiredPayload)) {
      if (v == null || v === "") {
        throw new Error(`logAdminAction: Missing or invalid payload field \`${k}\``);
      }
    }

    if (!description) {
      throw new Error("logAdminAction: `description` is required and cannot be empty");
    }

    // 4) Safely stringify logData (unchanged)
    let safeLogData;
    if (logData != null) {
      try {
        safeLogData =
          typeof logData === "string"
            ? JSON.stringify({ message: logData })
            : JSON.stringify(logData);
      } catch {
        console.warn("⚠️ logAdminAction: logData could not be stringified");
        safeLogData = JSON.stringify({ error: "logData could not be stringified" });
      }
    }

    // 5) Build final payload
    const evtPayload = {
      source,
      type,
      category,
      entityId,
      projectToken,
      description,
      ...(modelType && { modelType }),
      ...(safeLogData && { logData: safeLogData }),
      ...extras,

      // Authoritative Oz flags (callers never pass ozLog)
      ozLog: {
        isTicker: isTicker === true,
        isSuperAdmin: isSuperAdmin === true,
      },
    };

    if (evtPayload.projectToken === undefined) {
      evtPayload.projectToken = projectToken;
    }

    // 6) Raise the event
    await raiseEvent({
      event,
      sourceType,
      sourceId: finalSourceId,
      userId,
      projectToken,
      payload: evtPayload,
    });

    return true;
  } catch (err) {
    console.error("❌ Failed to log admin event:", event, err);
    return false;
  }
}

export async function raiseEvent({ event, sourceType, sourceId, userId, projectToken, payload }) {
  // 1) Top-level validation (required fields)
  for (const [k, v] of Object.entries({ event, sourceType, userId, projectToken })) {
    if (v === undefined || v === null || v === "") {
      throw new Error(`raiseEvent: Missing or invalid \`${k}\``);
    }
  }

  // 1b) sourceId is required by schema; default if not provided
  const finalSourceId = sourceId ?? `${sourceType}:${userId}`;
  if (finalSourceId === undefined || finalSourceId === null || finalSourceId === "") {
    throw new Error("raiseEvent: Missing or invalid `sourceId`");
  }

  // 2) Ensure we’ve got something to persist
  if (!payload || typeof payload !== "object") {
    throw new Error("raiseEvent: Missing or invalid `payload` object");
  }

  // 3) Ensure ozLog exists with safe defaults (do NOT override explicit values)
  const ozLog = payload.ozLog ?? {};
  payload.ozLog = {
    isTicker: ozLog.isTicker === true,
    isSuperAdmin: ozLog.isSuperAdmin === true,
  };

  // 3b) last-resort fallback — callers should supply real values
  payload.modelType = payload.modelType || ModelType.UNKNOWN;
  payload.category = payload.category || LogCategory.UNKNOWN;

  // 4) Compute a 90-day TTL (in epoch seconds)
  const expire_at = Math.floor((Date.now() + 90 * 24 * 60 * 60 * 1000) / 1000);

  // 5) Build the Events record
  const recordInput = {
    event,
    sourceType,
    sourceId: finalSourceId,
    projectToken,
    payload,
    ttl: expire_at,
    createdAt: new Date().toISOString(),
  };

  // 6) Persist
  await DataStore.start();
  const saved = await DataStore.save(new Events(recordInput));
  console.log("✅ Event saved:", saved);

  const local = await DataStore.query(Events, saved.id);
  console.log("Local DS query result:", local);

  return saved;
}

export async function raiseNewProjectCreatedEvent({
  userId,
  userEmail,
  accountToken,
  sourceProjectToken,
  newProject,
  submissionForm,
  reviewForm,
}) {
  if (!userId) throw new Error("raiseNewProjectCreatedEvent: missing userId");
  if (!userEmail) throw new Error("raiseNewProjectCreatedEvent: missing userEmail");
  if (!accountToken) throw new Error("raiseNewProjectCreatedEvent: missing accountToken");
  if (!sourceProjectToken) throw new Error("raiseNewProjectCreatedEvent: missing sourceProjectToken");

  if (!newProject || typeof newProject !== "object") {
    throw new Error("raiseNewProjectCreatedEvent: missing newProject");
  }
  if (!submissionForm || typeof submissionForm !== "object") {
    throw new Error("raiseNewProjectCreatedEvent: missing submissionForm");
  }
  if (!reviewForm || typeof reviewForm !== "object") {
    throw new Error("raiseNewProjectCreatedEvent: missing reviewForm");
  }

  const { name: newProjectName, token: newProjectToken, code: newProjectCode } = newProject;
  if (!newProjectName) throw new Error("raiseNewProjectCreatedEvent: missing newProject.name");
  if (!newProjectToken) throw new Error("raiseNewProjectCreatedEvent: missing newProject.token");
  if (!newProjectCode) throw new Error("raiseNewProjectCreatedEvent: missing newProject.code");

  const { name: submissionFormName, token: submissionFormToken } = submissionForm;
  if (!submissionFormName) throw new Error("raiseNewProjectCreatedEvent: missing submissionForm.name");
  if (!submissionFormToken) throw new Error("raiseNewProjectCreatedEvent: missing submissionForm.token");

  const { name: reviewFormName, token: reviewFormToken } = reviewForm;
  if (!reviewFormName) throw new Error("raiseNewProjectCreatedEvent: missing reviewForm.name");
  if (!reviewFormToken) throw new Error("raiseNewProjectCreatedEvent: missing reviewForm.token");

  // Explicit, stable sourceId for this semantic event
  const sourceId = "project:newProjectCreated";

  await raiseEvent({
    event: "newProjectCreated",
    sourceType: EventsSourceType.USER,
    sourceId,
    userId,
    projectToken: newProjectToken,
    payload: {
      userId,
      accountToken,
      projectToken: newProjectToken,
      email: userEmail,
      sourceProjectToken,
      newProject: {
        name: newProjectName,
        token: newProjectToken,
        code: newProjectCode,
      },
      submissionForm: {
        name: submissionFormName,
        token: submissionFormToken,
      },
      reviewForm: {
        name: reviewFormName,
        token: reviewFormToken,
      },

      modelType: ModelType.PROJECT,
      category: LogCategory.CREATED,

      setDescription: `Project cloned: ${newProjectToken}`,

      // Asset clone configuration
      assetClone: {
        mode: "ALL",
      },

      // Explicit ozLog intent (hard-coded by design decision)
      ozLog: {
        isTicker: false,
        isSuperAdmin: true,
      },
    },
  });

  return { ok: true, newProjectName, newProjectToken };
}


export async function upsertAssetsAndRaiseEvents({
  updatedAssetsMap,
  accountToken,
  projectToken,
  formToken,
  userId,
  uploadedBy,
  log = false,
}) {
  if (!updatedAssetsMap || typeof updatedAssetsMap !== "object" || Object.keys(updatedAssetsMap).length === 0) {
    return { ok: true, skipped: true, message: "nothing to save", count: 0, results: [] };
  }

  if (!accountToken) throw new Error("HSA: missing accountToken for asset save");
  if (!projectToken) throw new Error("HSA: missing projectToken for asset save");
  if (!formToken) throw new Error("HSA: missing formToken for asset save");
  if (!userId) throw new Error("HSA: missing userId for upsertAsset events");
  if (!uploadedBy) throw new Error("HSA: missing uploadedBy (required for AssetsRegistry)");

  // Validate shape: { [assetKey]: { content, type, language?, name?, description? } }
  for (const [assetKey, value] of Object.entries(updatedAssetsMap)) {
    if (!value || typeof value !== "object") {
      throw new Error(
        `HSA: updatedAssetsMap["${assetKey}"] must be an object { content, type, language?, name?, description? }`
      );
    }
    if (!Object.prototype.hasOwnProperty.call(value, "content")) {
      throw new Error(`HSA: updatedAssetsMap["${assetKey}"] is missing required "content"`);
    }
    if (!value.content || typeof value.content !== "object") {
      throw new Error(`HSA: updatedAssetsMap["${assetKey}"].content must be a non-null object/array`);
    }
    if (!value.type || typeof value.type !== "string") {
      throw new Error(`HSA: updatedAssetsMap["${assetKey}"].type is required and must be a non-empty string`);
    }
  }

  const results = await saveAssetsBatch({
    updatedAssetsMap,
    accountToken,
    projectToken,
    formToken,
    DataStore,
    defaultUploadedBy: uploadedBy,
    defaultUserId: userId,
  });

  // Explicit, stable sourceId for asset upsert events
  const sourceId = `${EventsSourceType.USER}:${userId}`;

  if (Array.isArray(results) && results.length > 0) {
    for (const item of results) {
      if (!item || !item.assetPayload) {
        if (log) console.warn("HSA: skipping invalid saveAssetsBatch result:", item);
        continue;
      }

      const { operation, assetPayload } = item;
      const isUpdate = operation === "update";

      const { id, key, name, type, fileType, fileSize } = assetPayload;
      const assetName = name || key;

      const setDescription = isUpdate
        ? `Asset ${assetName} (${key}) updated.`
        : `Asset ${assetName} (${key}) created.`;

      const logData = {
        operation,
        isUpdate,
        id: id || null,
        key,
        type,
        name,
        projectToken,
        accountToken,
        formToken,
        fileType,
        fileSize,
      };

      if (log) {
        console.log("UA: upsertAsset event", {
          sourceId,
          payloadPreview: { key, name, type, setDescription },
        });
      }

      await raiseEvent({
        event: "UpsertAsset",
        sourceType: EventsSourceType.USER,
        sourceId,
        userId,
        projectToken,
        payload: {
          ...assetPayload,
          setDescription,
          logData,
          modelType: ModelType.ASSET,
          category: LogCategory.UPDATED,

          // Explicit ozLog intent
          ozLog: {
            isTicker: true,
            isSuperAdmin: false,
          },
        },
      });
    }
  }

  return {
    ok: true,
    skipped: false,
    count: Array.isArray(results) ? results.length : 0,
    results: Array.isArray(results) ? results : [],
  };
}

/**
 * Trigger Dawn AI summary generation for a user
 * 
 * @param {Object} params
 * @param {string} params.userId - User ID to generate summary for
 * @param {string} params.projectToken - Project token
 * @param {string} params.submissionFormToken - Submission form token
 * @param {string} params.reviewFormToken - Review form token
 * @param {string} [params.userEmail] - User email (optional, for logging)
 * @param {string} [params.userGivenName] - User given name (optional, for logging)
 * @param {string} [params.userFamilyName] - User family name (optional, for logging)
 * @param {boolean} [params.manual=false] - Whether this was manually triggered
 * @returns {Promise<boolean>} Success status
 */
export async function triggerDawnSummary({
  userId,
  projectToken,
  submissionFormToken,
  reviewFormToken,
  userEmail,
  userGivenName,
  userFamilyName,
  manual = false,
}) {
  try {
    // 1) Validate required fields
    if (!userId || typeof userId !== "string") {
      throw new Error("triggerDawnSummary: Missing or invalid `userId`");
    }
    if (!projectToken || typeof projectToken !== "string") {
      throw new Error("triggerDawnSummary: Missing or invalid `projectToken`");
    }
    if (!submissionFormToken || typeof submissionFormToken !== "string") {
      throw new Error("triggerDawnSummary: Missing or invalid `submissionFormToken`");
    }
    if (!reviewFormToken || typeof reviewFormToken !== "string") {
      throw new Error("triggerDawnSummary: Missing or invalid `reviewFormToken`");
    }

    // 2) Build user display name for logging
    const userName = userGivenName && userFamilyName
      ? `${userGivenName} ${userFamilyName}`
      : userEmail || userId;

    // 3) Build description
    const description = manual
      ? `Manual Dawn summary trigger for ${userName}`
      : `Generating Dawn AI summary for ${userName}`;

    // 4) Build logData
    const logData = {
      userId,
      projectToken,
      submissionFormToken,
      reviewFormToken,
      requestedAt: new Date().toISOString(),
      manual,
      ...(userEmail && { email: userEmail }),
      ...(userGivenName && { givenName: userGivenName }),
      ...(userFamilyName && { familyName: userFamilyName }),
    };

    // 5) Explicit, stable sourceId for Dawn events
    const sourceId = `dawn:${userId}`;

    // 6) Raise the event
    await raiseEvent({
      event: "DawnSummaryRequested",
      sourceType: EventsSourceType.AI,
      sourceId,
      userId,
      projectToken,
      payload: {
        source: "DawnAI",
        type: "INFO",
        category: "AI_SUGGESTION",
        entityId: userId,
        projectToken,
        modelType: "USER",
        description,
        setDescription: manual
          ? "Dawn AI summary generation (manual)"
          : "Dawn AI summary generation initiated",
        
        // Required fields for ozMetricsSummary lambda
        userId,
        submissionFormToken,
        reviewFormToken,
        
        logData: JSON.stringify(logData),

        // Explicit ozLog intent
        ozLog: {
          isTicker: false,      // Don't show in ticker - internal process
          isSuperAdmin: false,  // Not a super admin action
        },
      },
    });

    console.log("✅ Dawn summary event raised for user:", userId);
    return true;

  } catch (err) {
    console.error("❌ Failed to trigger Dawn summary:", err);
    return false;
  }
}